<template>
  <!-- eslint-disable vue/no-v-html -- linkify escapes HTML, so there is no risk of XSS from this content -->
  <component
    :is="renderElement"
    v-if="!info && hotlinkUrls"
    :class="['own-type', renderClass]"
    v-html="linkifiedText"
  />
  <!-- eslint-enable -->

  <component
    :is="renderElement"
    v-else-if="!info && !hotlinkUrls"
    :class="['own-type', renderClass]"
    :title="title"
    v-text="text"
  />

  <OwnTypePopover
    v-else
    :align="align"
    :render-class="renderClass"
    :render-element="renderElement"
    :tabular="tabular"
    :text="text"
    :info="info"
    :tooltip-align-x="infoDirection"
  />
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { linkify } from '@/utils/linkify'

import { lookupFontColor, FontColor, FontType } from '../Common/fonts'

import OwnTypePopover from './OwnTypePopover.vue'

const props = withDefaults(
  defineProps<{
    /**
     * @default 'left'
     */
    align?: 'left' | 'center' | 'right'
    color?: FontColor
    /**
     * Element to render as. When not specified, an appropriate element type will be chosen based on the `variant`.
     */
    el?: // Should be `Extract<keyof HTMLElementTagNameMap, ...>`, but that breaks Vue's prop type inference
      | 'caption'
      | 'h1'
      | 'h2'
      | 'h3'
      | 'h4'
      | 'h5'
      | 'h6'
      | 'label'
      | 'li'
      | 'p'
      | 'span'
      | 'summary'
      | 'td'
      | 'th'
      | 'a'

    /**
     * @default false
     */
    hotlinkUrls?: boolean
    info?: string
    infoDirection?: 'left' | 'right'
    /**
     * @default false
     */
    preserveWhitespace?: boolean
    monospace?: boolean
    tabular?: boolean
    text: string | number
    /**
     * @default false
     */
    textWrap?: boolean
    title?: string
    /**
     * @default 'paragraph'
     */
    variant?: FontType
  }>(),
  {
    align: 'left',
    color: undefined,
    el: undefined,
    hotlinkUrls: false,
    info: undefined,
    infoDirection: 'right',
    monospace: false,
    preserveWhitespace: false,
    tabular: false,
    textWrap: false,
    title: undefined,
    variant: 'paragraph-regular',
  }
)

const colorClass = computed(() => {
  if (props.color) {
    return lookupFontColor(props.color)
  }

  switch (props.variant) {
    case 'heading-regular':
    case 'heading-small':
    case 'title-large':
    case 'title-regular':
      return 'text-color-primary'
    default:
      return 'text-color-secondary'
  }
})

const linkifiedText = computed(() => {
  if (props.hotlinkUrls) {
    return linkify(props.text.toString(), {
      className: 'own-type__link',
    })
  }

  return props.text.toString()
})

const typeClass = computed(() => {
  const standardClass = `text-${props.variant}`

  if (props.monospace) {
    return `${standardClass} text--mono`
  }

  return standardClass
})

const renderClass = computed(() => {
  const classes = [typeClass.value, colorClass.value, `text--${props.align}`]

  if (props.tabular) {
    classes.push('tabular-nums')
  }

  if (props.preserveWhitespace) {
    classes.push(`preserve-whitespace`)
  }

  if (props.textWrap) {
    classes.push('text-wrap')
  }

  return classes.join(' ')
})

const renderElement = computed(() => {
  if (props.el) return props.el

  switch (props.variant) {
    case 'heading-regular':
      return 'h1'
    case 'heading-small':
      return 'h2'
    case 'title-large':
      return 'h3'
    case 'title-regular':
      return 'h3'
    case 'title-small':
      return 'h6'
    case 'button-regular':
      return 'span'
    default:
      return 'p'
  }
})
</script>

<style lang="scss">
.own-type {
  &.tabular-nums {
    font-variant-numeric: tabular-nums;
  }

  &.preserve-whitespace {
    white-space: pre;

    // When combined with text-wrap, allow wrapping while preserving whitespace
    // This prevents text overflow in constrained containers (e.g., SMS previews)
    &.text-wrap {
      white-space: pre-wrap;
    }
  }

  &.text-wrap {
    overflow-wrap: break-word; // modern browsers
    word-wrap: break-word; // fallback for older browsers
    hyphens: auto; // enable hyphenation for long words
  }

  &.own-type__link {
    color: var(--misc-brand);
  }
}
</style>
